import { useState, useEffect, useCallback, useMemo, type ReactElement, Fragment } from 'react'
import { throttle } from 'lodash'
import i18n from 'i18n'

// Map imports
import { useMap, type MapLayerMouseEvent } from 'react-map-gl/maplibre'

// @mui imports
import Popper, { type PopperProps } from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'

// KN imports
import type {
  KNMapLibreTooltipProps,
  KNMapLibreTooltipWrapperProps,
  MapMarker,
  MapTooltip,
} from './types'
import {
  getMarkerTypeIconComponent,
  getMarkerTypeColor,
  getMarkerTypeLabel,
  getMarkerIcon,
} from './helpers'

const getTooltipContent = (marker: MapMarker): ReactElement => (
  <KNMapLibreTooltipWrapper
    content={marker.tooltip}
    icon={getMarkerIcon(marker, 24)}
  />
)

const getTooltipGroupContent = (type: string, markers: MapMarker[]): ReactElement => {
  const getContent = (type: string): ReactElement[] => {
    const subgroups = markers.reduce((subgroups: Map<string, MapMarker[]>, marker) => {
      const markerSubType = marker.subType ?? '_'
      const currentSubgroup = subgroups.get(markerSubType) ?? []
      currentSubgroup.push(marker)
      subgroups.set(markerSubType, currentSubgroup)
      return subgroups
    }, new Map<string, MapMarker[]>())

    const tooltipsContent: ReactElement[] = []
    subgroups.forEach((markers, subgroup) => {
      if (subgroup === '_') {
        // display each tooltip without subgroup as a separate line
        markers.forEach((marker) => {
          tooltipsContent.push(marker.compactTooltip!)
        })
      } else {
        // otherwise display single line with a counter for whole subgroup
        tooltipsContent.push((
          <Stack spacing={0.5} direction="row">
            {markers[0]?.compactTooltip}
            <Typography variant="p5" color="primary.light">&times;{markers.length}</Typography>
          </Stack>
        ))
      }
    })
    return tooltipsContent
  }
  const MarkerTypeIcon = getMarkerTypeIconComponent(type)
  return (
    <KNMapLibreTooltipWrapper
      content={(
        <>
          <Typography component="p" variant="p4" color="primary.main">
            {i18n.t(`screens.cs.trip_details.map.types.${type}`)}
          </Typography>
          {getContent(type)}
        </>
      )}
      icon={(
        <MarkerTypeIcon size={24} color={getMarkerTypeColor(type)} label={getMarkerTypeLabel(type)} />
      )}
    />
  )
}

const KNMapLibreTooltipWrapper = ({
  content,
  icon,
}: KNMapLibreTooltipWrapperProps): ReactElement => (
  <Box paddingY={0.75} paddingLeft={1} paddingRight={2}>
    <Stack spacing={1} direction="row" alignItems="start">
      {icon && (
        <Box sx={{ width: '24px', height: '24px', flexShrink: 0 }}>
          {icon}
        </Box>
      )}
      {content && (
        <Box>
          {content}
        </Box>
      )}
    </Stack>
  </Box>
)

const KNMapLibreTooltip = ({
  markers,
}: KNMapLibreTooltipProps): ReactElement | null => {
  const { current: map } = useMap()
  const [tooltips, setTooltips] = useState<MapTooltip[]>([])
  const [anchor, setAnchor] = useState<PopperProps['anchorEl'] | null>(null)

  useEffect(() => {
    map?.on('mousemove', handleTooltipMoveThrottled)
    map?.on('mousemove', handleTooltipContentThrottled)

    return () => {
      map?.off('mousemove', handleTooltipMoveThrottled)
      map?.off('mousemove', handleTooltipContentThrottled)
    }
  }, [map, markers])

  const handleTooltipMove = useCallback((event: MapLayerMouseEvent) => {
    setAnchor({
      getBoundingClientRect: () => new DOMRect(event.originalEvent.clientX, event.originalEvent.clientY, 0, 0),
    })
  }, [])
  const handleTooltipMoveThrottled = useMemo(
    () => throttle(handleTooltipMove, 16),
    [handleTooltipMove]
  )

  const handleTooltipContent = useCallback(() => {
    const tooltips: MapTooltip[] = []

    const standaloneMarkers = markers.filter((marker) => !marker.grouped && marker.tooltip)
    standaloneMarkers.forEach((marker) => {
      tooltips.push({
        id: marker.id,
        content: getTooltipContent(marker),
      })
    })

    // group markers by their types
    const groupableMarkers = markers.filter((marker) => marker.grouped && marker.compactTooltip)
    const markerGroups = groupableMarkers.reduce((groups: Map<string, MapMarker[]>, marker) => {
      const markerType = marker.type ?? 'GENERIC'
      const currentGroup = groups.get(markerType) ?? []
      currentGroup.push(marker)
      groups.set(markerType, currentGroup)
      return groups
    }, new Map<string, MapMarker[]>())
    markerGroups.forEach((markers, type) => {
      tooltips.push({
        id: type,
        content: getTooltipGroupContent(type, markers),
      })
    })

    setTooltips(tooltips)
  }, [markers])
  const handleTooltipContentThrottled = useMemo(
    () => throttle(handleTooltipContent, 200, { leading: true, trailing: false }),
    [handleTooltipContent, markers]
  )

  return (
    <Popper
      open={Boolean(anchor)}
      disablePortal={true}
      anchorEl={anchor}
      placement="auto-start"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [8, 8],
          },
        },
      ]}
      sx={{
        pointerEvents: 'none',
        zIndex: 2000,
      }}
    >
      <Paper sx={{ maxWidth: '20rem' }}>
        <Stack
          direction="column"
          divider={<Divider orientation="horizontal" flexItem />}
        >
          {tooltips.map((tooltip) => (
            <Fragment key={tooltip.id}>
              {tooltip.content}
            </Fragment>
          ))}
        </Stack>
      </Paper>
    </Popper>
  )
}

export default KNMapLibreTooltip
